import React, { useState, useEffect } from "react";
import dayjs from 'dayjs';
import {
    Grid, TextField, FormHelperText, Typography, Box, Autocomplete, Chip, useMediaQuery,
    Select, MenuItem, Button, Checkbox, ToggleButton, ToggleButtonGroup, Stack
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { useLocation } from "react-router-dom";

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import RemoveIcon from "@mui/icons-material/RemoveCircleOutlineSharp";

import * as CONSTANTS from "../../constants"

import { 
    view_wazaren_namelist,
    view_wotageishi_namelist, 
    import_wazaren_data 
} from "../../services/topic_view";


const FillInForm = ({ formik, lang }) => {

    useEffect(() => { window.scrollTo(0, 0) }, []);

    const location = useLocation();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));  // Mobile detection

    const [tags, setTags] = useState(formik.values.tags);
    const [creators, setCreators] = useState([]);
    const [creator, setCreator] = useState(null);
    const [editors, setEditors] = useState([]);
    const [editor, setEditor] = useState(null);
    const [targetWazaren, setTargetWazaren] = useState(null)
    const [wazarens, setWazarens] = useState([])
    const [currentWazaren, setCurrentWazaren] = useState(null);

    const updateTags = (tags) => {
        setTags(tags);
        formik.values.tags = tags;
    }
    const updateCreatorUid = (creator) => {
        if (creator !== null) {
            formik.values.creator_uid = creator.uid;
            formik.values.creator_name = creator.name;
        } else {
            formik.values.creator_uid = 0;
            formik.values.creator_name = '';
        }
    }
    const updateEditorUid = (editor) => {
        if (editor !== null) {
            formik.values.editor_uid = editor.uid;
            formik.values.editor_name = editor.name;
        } else {
            formik.values.editor_uid = 0;
            formik.values.editor_name = '';
        }
    }

    const updateWazarenUid = (wazaren) => {
        setTargetWazaren(wazaren);
        if (wazaren === null) {
            formik.values.wazaren_uid = 0;
        } else {
            formik.values.wazaren_uid = wazaren.uid;
        }
    }

    const updateWazarenTextField = (newValue) => {
        fetchWazarens(newValue);
    }
    const updateCreatorTextField = (newValue) => {
        fetchCreator(newValue);
    }
    const updateEditorTextField = (newValue) => {
        fetchEditor(newValue);
    }

    const fetchCreator = async (name) => {
        const [status, result] = await view_wotageishi_namelist(lang, name);
        if (status === 200) {
            setCreators(result.wotageishis);
        }
    };
    
    const fetchEditor = async (name) => {
        const [status, result] = await view_wotageishi_namelist(lang, name);
        if (status === 200) {
            setEditors(result.wotageishis);
        }
    };

    const fetchWazarenDataByUid = async (uid) => {
        const [status, result] = await import_wazaren_data(lang, uid);
        if (status === 200) {
            setCurrentWazaren(result);
            autoFillFormik(result);
        }
    }

    const autoFillFormik = (currentWazaren) => {
        if (currentWazaren === null) return
        
        // Update formik values one by one
        formik.values.name_en = currentWazaren.name.en;
        formik.values.name_jp = currentWazaren.name.jp;
        formik.values.name_tc = currentWazaren.name.tc;
        formik.values.name_sc = currentWazaren.name.sc;
        formik.values.date_created = dayjs(currentWazaren.date_created.value);
        formik.values.has_year = currentWazaren.date_created.has_year;
        formik.values.has_month = currentWazaren.date_created.has_month;
        formik.values.has_day = currentWazaren.date_created.has_day;
        formik.values.default_name = currentWazaren.default_name;
        formik.values.creator_uid = currentWazaren.creator_uid;
        formik.values.creator_name = currentWazaren.creator_name;
        formik.values.editor_uid = currentWazaren.editor_uid;
        formik.values.editor_name = currentWazaren.editor_name;
        formik.values.description_en = currentWazaren.description.en;
        formik.values.description_jp = currentWazaren.description.jp;
        formik.values.description_tc = currentWazaren.description.tc;
        formik.values.description_sc = currentWazaren.description.sc;
        formik.values.video_youtube = currentWazaren.video_source.youtube;
        formik.values.video_bilibili = currentWazaren.video_source.bilibili;
        formik.values.video_x = currentWazaren.video_source.x;
        formik.values.remark_youtube = currentWazaren.video_remark.youtube;
        formik.values.remark_bilibili = currentWazaren.video_remark.bilibili;
        formik.values.remark_x = currentWazaren.video_remark.x;
        formik.values.tags = currentWazaren.tags;
        setTags(currentWazaren.tags);
    }

    const fetchWazarens = async (name) => {
        const [status, result] = await view_wazaren_namelist(lang, name)
        if (status === 200) {
            setWazarens(result.wazarens);
        }
    };

    // send api request for url changes
    useEffect(() => {
        fetchCreator('');
        fetchEditor('');
        fetchWazarens('');
    }, [location.pathname]);


    const UpdateDefaultName = (option) => {
        switch (option) {
            case 'en':
                formik.values.default_name = formik.values.name_en;
                break;
            case 'jp':
                formik.values.default_name = formik.values.name_jp;
                break;
            case 'tc':
                formik.values.default_name = formik.values.name_tc;
                break;
            case 'sc':
                formik.values.default_name = formik.values.name_sc;
                break;
        }
    }

    const radioButtonMap = {
        'en': CONSTANTS.REQUEST_TOPIC.FIELDS.LANG.EN[lang], 
        'jp': CONSTANTS.REQUEST_TOPIC.FIELDS.LANG.JP[lang], 
        'tc': CONSTANTS.REQUEST_TOPIC.FIELDS.LANG.TC[lang],
        'sc': CONSTANTS.REQUEST_TOPIC.FIELDS.LANG.SC[lang], 
    };
    
    let tagKeys = []
    for (let index = 1; index <= Object.keys(CONSTANTS.WAZAREN_TAGS).length; index++) {
        tagKeys.push(index);
    }

    return (
        <Grid
            container
            spacing={2}
        >
            <Grid
                item
                xs={12}
                justifyContent={'left'}
            >
                <Typography
                    backgroundColor="#7e7e7e"
                    color="white"
                    fontWeight="bold"
                    p={2}
                >
                    {CONSTANTS.REQUEST_TOPIC.FIELDS.WAZAREN_UID[lang].toUpperCase()}
                </Typography>
            </Grid>
            <Grid
                item
                xs={4}
                alignContent={'center'}
            >   
                <Typography p={2}>{CONSTANTS.REQUEST_TOPIC.FIELDS.WAZAREN_UID[lang]}</Typography>
            </Grid>
            <Grid
                item
                xs={8}
                sm={4}
                alignContent={'center'}
            >
                <Autocomplete
                    id="wazarens-standard"
                    freeSolo
                    filterSelectedOptions
                    options={wazarens}
                    onChange={(e, wazaren) =>updateWazarenUid(wazaren)}
                    getOptionLabel={wazaren => wazaren.name}
                    renderTags={() => {}}
                    value={targetWazaren}
                    componentsProps={{
                        paper: {
                            sx: {
                                width: '100%'
                            }
                        }
                    }}
                    renderInput={params => (
                    <TextField
                        {...params}
                        variant="standard"
                        placeholder=""
                        margin="normal"
                        fullWidth
                        onChange={(e) => {
                            updateWazarenTextField(e.target.value);
                        }}
                    />
                    )}
                />
            </Grid>
            { isMobile? <Grid item xs={4} />: <></> }
            <Grid
                item
                xs={8}
                sm={4}
                alignContent={'center'}
            >
                <Box
                    name="Creator Result"
                    variant="outlined"
                    type="text"
                    fullWidth
                    sx={{p: 1, border: '1px solid grey', borderRadius: '5px' }}
                >
                    <Typography
                        textAlign="center"
                    >
                        {formik.values.wazaren_uid!==0? formik.values.wazaren_uid: '---'}
                    </Typography>
                </Box>
            </Grid>
            <Grid
                item
                xs={12}
                textAlign={'center'}
                justifyContent={'center'}
            >
                <Button
                    variant="outlined"
                    disabled={formik.values.wazaren_uid===0}
                    onClick={()=> {
                        fetchWazarenDataByUid(formik.values.wazaren_uid)
                    }}
                    sx={{ width: '50%' }}
                >
                    {CONSTANTS.REQUEST_TOPIC.BUTTONS.LOAD_WAZAREN_DATA[lang]}
                </Button>
            </Grid>
            <Grid
                item
                xs={12}
                justifyContent={'left'}
            >
                <Typography
                    backgroundColor="#7e7e7e"
                    color="white"
                    fontWeight="bold"
                    p={2}
                >
                    {CONSTANTS.REQUEST_TOPIC.FIELDS.NAME[lang]}
                </Typography>
            </Grid>
            <Grid
                item
                xs={4}
                alignContent={'center'}
            >   
                <Typography p={2}>
                    {CONSTANTS.REQUEST_TOPIC.FIELDS.LANG.EN[lang]}
                </Typography>
            </Grid>
            <Grid
                item
                xs={8}
                alignContent={'center'}
                pr={2}
            >   
                <TextField
                    name="English Name"
                    variant="outlined"
                    placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.NAME.EN[lang]}
                    type="text"
                    fullWidth
                    size="small"
                    error={Boolean(formik.touched.name_en && formik.errors.name_en)}
                    helperText={formik.errors.name_en && formik.errors.name_en}
                    onChange={(e) => {
                        formik.handleChange("name_en")(e);
                        if (formik.values.default_language === 'en') {
                            formik.handleChange("default_name")(e);
                        }
                    }}
                    value={formik.values.name_en}
                />
            </Grid>
            <Grid
                item
                xs={4}
                alignContent={'center'}
            >   
                <Typography p={2}>
                    {CONSTANTS.REQUEST_TOPIC.FIELDS.LANG.JP[lang]}
                </Typography>
            </Grid>
            <Grid
                item
                xs={8}
                alignContent={'center'}
                pr={2}
            >   
                <TextField
                    name="Japanese Name"
                    variant="outlined"
                    placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.NAME.JP[lang]}
                    type="text"
                    fullWidth
                    size="small"
                    error={Boolean(formik.touched.name_jp && formik.errors.name_jp)}
                    helperText={formik.errors.name_jp && formik.errors.name_jp}
                    onChange={(e) => {
                        formik.handleChange("name_jp")(e);
                        if (formik.values.default_language === 'jp') {
                            formik.handleChange("default_name")(e);
                        }
                    }}
                    value={formik.values.name_jp}
                />
            </Grid>
            <Grid
                item
                xs={4}
                alignContent={'center'}
            >   
                <Typography p={2}>
                    {CONSTANTS.REQUEST_TOPIC.FIELDS.LANG.TC[lang]}
                </Typography>
            </Grid>
            <Grid
                item
                xs={8}
                alignContent={'center'}
                pr={2}
            >   
                <TextField
                    name="Traditional Chinese Name"
                    variant="outlined"
                    placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.NAME.TC[lang]}
                    type="text"
                    fullWidth
                    size="small"
                    error={Boolean(formik.touched.name_tc && formik.errors.name_tc)}
                    helperText={formik.errors.name_tc && formik.errors.name_tc}
                    onChange={(e) => {
                        formik.handleChange("name_tc")(e);
                        if (formik.values.default_language === 'tc') {
                            formik.handleChange("default_name")(e);
                        }
                    }}
                    value={formik.values.name_tc}
                />
            </Grid>
            <Grid
                item
                xs={4}
                alignContent={'center'}
            >   
                <Typography p={2}>
                    {CONSTANTS.REQUEST_TOPIC.FIELDS.LANG.SC[lang]}
                </Typography>
            </Grid>
            <Grid
                item
                xs={8}
                alignContent={'center'}
                pr={2}
            >   
                <TextField
                    name="Simplified Chinese Name"
                    variant="outlined"
                    placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.NAME.SC[lang]}
                    type="text"
                    fullWidth
                    size="small"
                    error={Boolean(formik.touched.name_sc && formik.errors.name_sc)}
                    helperText={formik.errors.name_sc && formik.errors.name_sc}
                    onChange={(e) => {
                        formik.handleChange("name_sc")(e);
                        if (formik.values.default_language === 'sc') {
                            formik.handleChange("default_name")(e);
                        }
                    }}
                    value={formik.values.name_sc}
                />
            </Grid>
            <Grid
                item
                xs={4}
                alignContent={'center'}
            >   
                <Typography p={2}>{CONSTANTS.REQUEST_TOPIC.FIELDS.DEFAULT_LANG[lang]}</Typography>
            </Grid>
            <Grid
                item
                xs={8}
                alignContent={'center'}
                pr={2}
            >   
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }} >
                    {
                        isMobile?
                        <Select
                            value={formik.values.default_language}
                            onChange={(event) => {
                                UpdateDefaultName(event.target.value);
                                formik.setFieldValue('default_language', event.target.value);
                            }}
                            sx={{width: "100%"}}
                        >
                            {Object.keys(radioButtonMap).map((key, index) => (
                                <MenuItem
                                    value={key}
                                >
                                    {radioButtonMap[key]}
                                </MenuItem>
                            ))}
                        </Select>
                        :
                        <ToggleButtonGroup
                            orientation="horizontal"
                            color="primary"
                            value={formik.values.default_language}
                            onChange={(event) => {
                                UpdateDefaultName(event.target.value);
                                formik.setFieldValue('default_language', event.target.value);
                            }}
                            exclusive
                            sx={{
                                minHeight: 48,
                                padding: '4px',
                                borderRadius: '12px',
                                width: "100%",
                            }}
                        >
                            {Object.keys(radioButtonMap).map((key, index) => (
                                <ToggleButton
                                    key={key}
                                    value={key}
                                    disableIcon
                                    variant="plain"
                                    sx={{ px: 2, alignItems: 'center', width: '25%', p: 1.5, textAlign: 'center'}}
                                    
                                >{radioButtonMap[key]}</ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    }
                </Box>
            </Grid>
            <Grid
                mt={3}
                item
                xs={12}
                justifyContent={'left'}
            >
                <Typography
                    backgroundColor="#7e7e7e"
                    color="white"
                    fontWeight="bold"
                    p={2}
                >
                    {CONSTANTS.WAZAREN_DETAILS.DESCRIPTION[lang]}
                </Typography>
            </Grid>
            <Grid
                item
                xs={4}
                alignContent={'center'}
            >   
                <Typography p={2}>
                    {CONSTANTS.REQUEST_TOPIC.FIELDS.LANG.EN[lang]}
                </Typography>
            </Grid>
            <Grid
                item
                xs={8}
                alignContent={'center'}
                pr={2}
            >   
                <TextField
                    name="English Name"
                    variant="outlined"
                    placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.DESCRIPTION.EN[lang]}
                    type="text"
                    fullWidth
                    size="small"
                    error={Boolean(formik.touched.description_en && formik.errors.description_en)}
                    onChange={formik.handleChange('description_en')}
                    value={formik.values.description_en}
                />
            </Grid>
            <Grid
                item
                xs={4}
                alignContent={'center'}
            >   
                <Typography p={2}>
                    {CONSTANTS.REQUEST_TOPIC.FIELDS.LANG.JP[lang]}
                </Typography>
            </Grid>
            <Grid
                item
                xs={8}
                alignContent={'center'}
                pr={2}
            >   
                <TextField
                    name="Japanese Name"
                    variant="outlined"
                    placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.DESCRIPTION.JP[lang]}
                    type="text"
                    fullWidth
                    size="small"
                    error={Boolean(formik.touched.description_jp && formik.errors.description_jp)}
                    onChange={formik.handleChange('description_jp')}
                    value={formik.values.description_jp}
                />
            </Grid>
            <Grid
                item
                xs={4}
                alignContent={'center'}
            >   
                <Typography p={2}>
                    {CONSTANTS.REQUEST_TOPIC.FIELDS.LANG.TC[lang]}
                </Typography>
            </Grid>
            <Grid
                item
                xs={8}
                alignContent={'center'}
                pr={2}
            >   
                <TextField
                    name="Traditional Chinese Name"
                    variant="outlined"
                    placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.DESCRIPTION.TC[lang]}
                    type="text"
                    fullWidth
                    size="small"
                    error={Boolean(formik.touched.description_tc && formik.errors.description_tc)}
                    onChange={formik.handleChange('description_tc')}
                    value={formik.values.description_tc}
                />
            </Grid>
            <Grid
                item
                xs={4}
                alignContent={'center'}
            >   
                <Typography p={2}>
                    {CONSTANTS.REQUEST_TOPIC.FIELDS.LANG.SC[lang]}
                </Typography>
            </Grid>
            <Grid
                item
                xs={8}
                alignContent={'center'}
                pr={2}
            >   
                <TextField
                    name="Simplified Chinese Name"
                    variant="outlined"
                    placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.DESCRIPTION.SC[lang]}
                    type="text"
                    fullWidth
                    size="small"
                    error={Boolean(formik.touched.description_sc && formik.errors.description_sc)}
                    onChange={formik.handleChange('description_sc')}
                    value={formik.values.description_sc}
                />
            </Grid>
            <Grid
                mt={3}
                item
                xs={12}
                justifyContent={'left'}
            >
                <Typography
                    backgroundColor="#7e7e7e"
                    color="white"
                    fontWeight="bold"
                    p={2}
                >
                    {CONSTANTS.REQUEST_TOPIC.FIELDS.OTHER[lang]}
                </Typography>
            </Grid>
            {/* Need to add search creator uid by name */}
            <Grid
                item
                xs={4}
                alignContent={'center'}
            >   
                <Typography p={2}>{CONSTANTS.WAZAREN_DETAILS.CREATOR[lang]}</Typography>
            </Grid>
            <Grid
                item
                xs={8}
                sm={4}
                alignContent={'center'}
                pr={2}
            >   
                <Autocomplete
                    id="creator_name_list-standard"
                    freeSolo
                    options={creators}
                    onChange={(e, newValue) => {
                        setCreator(newValue);
                        updateCreatorUid(newValue);
                    }}
                    onReset={() => {
                        setCreator(null)
                        updateCreatorUid(null);
                    }}
                    getOptionLabel={item => item.name? item.name: ''}
                    renderTags={() => {}}
                    value={creator === null? '': creator.name}
                    componentsProps={{
                        paper: {
                            sx: {
                                width: '100%'
                            }
                        }
                    }}
                    renderInput={params => (
                    <TextField
                        {...params}
                        name="Creator"
                        variant="outlined"
                        placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.SEARCH.CREATOR[lang]}
                        type="text"
                        fullWidth
                        size="small"
                        error={Boolean(formik.touched.creator_uid && formik.errors.creator_uid)}
                        margin="normal"
                        onChange={(e) => {
                            updateCreatorTextField(e.target.value);
                        }}
                    />
                    )}
                />
            </Grid>
            { isMobile? <Grid item xs={4} />: <></> }
            <Grid
                item
                xs={8}
                sm={4}
                alignContent={'center'}
                pr={2}
            >   
                <Box
                    name="Creator Result"
                    variant="outlined"
                    type="text"
                    fullWidth
                    sx={{p: 1, border: '1px solid grey', borderRadius: '5px' }}
                    
                >
                    <Typography
                        textAlign="center"
                    >
                        {formik.values.creator_name !==''? formik.values.creator_name:'---'}
                    </Typography>
                </Box>
            </Grid>
            <Grid
                item
                xs={4}
                alignContent={'center'}
            >   
                <Typography p={2}>{CONSTANTS.WAZAREN_DETAILS.EDITOR[lang]}</Typography>
            </Grid>
            <Grid
                item
                xs={8}
                sm={4}
                alignContent={'center'}
                pr={2}
            >   
                <Autocomplete
                    id="editor_name_list-standard"
                    freeSolo
                    options={editors}
                    onChange={(e, newValue) => {
                        setEditor(newValue);
                        updateEditorUid(newValue);
                    }}
                    onReset={() => {
                        setEditor(null)
                        updateEditorUid(null);
                    }}
                    getOptionLabel={item => item.name? item.name: ''}
                    renderTags={() => {}}
                    value={editor === null? '': editor.name}
                    componentsProps={{
                        paper: {
                            sx: {
                                width: '100%'
                                
                            }
                        }
                    }}
                    renderInput={params => (
                    <TextField
                        {...params}
                        name="Editor"
                        variant="outlined"
                        placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.SEARCH.EDITOR[lang]}
                        type="text"
                        fullWidth
                        size="small"
                        error={Boolean(formik.touched.editor_uid && formik.errors.editor_uid)}
                        margin="normal"
                        onChange={(e) => {
                            updateEditorTextField(e.target.value);
                        }}
                    />
                    )}
                />
            </Grid>
            { isMobile? <Grid item xs={4} />: <></> }
            <Grid
                item
                xs={8}
                sm={4}
                alignContent={'center'}
                pr={2}
            >   
                <Box
                    name="Editor Result"
                    variant="outlined"
                    type="text"
                    fullWidth
                    sx={{p: 1, border: '1px solid grey', borderRadius: '5px' }}
                    
                >
                    <Typography
                        textAlign="center"
                    >
                        {formik.values.editor_name !==''? formik.values.editor_name:'---'}
                    </Typography>
                </Box>
            </Grid>
            <Grid
                item
                xs={4}
                alignContent={'center'}
            >   
                <Typography p={2}>{CONSTANTS.WAZAREN_DETAILS.DATE_CREATED[lang]}</Typography>
            </Grid>
            <Grid
                item
                xs={8}
                md={4}
                alignContent={'center'}
            >   
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker 
                        format="DD/MM/YYYY"
                        onChange={(e)=>{formik.values.date_created = e}}
                        value={formik.values.date_created}
                        sx={{width: '100%'}}
                    />
                </LocalizationProvider>
            </Grid>
            { isMobile? <Grid item xs={4} />: <></> }
            <Grid
                item
                xs={8}
                sm={4}
                alignContent={'center'}
            >   
                <Stack 
                    direction={"row"} 
                    width={'100%'} 
                    margin={'auto'} 
                    flex={1} 
                    justifyContent="space-evenly"
                >
                    <Stack alignItems="center">
                        <Typography variant="body2">{CONSTANTS.REQUEST_TOPIC.FIELDS.YEAR[lang]}</Typography>
                        <Checkbox 
                            checked={formik.values.has_year}
                            onChange={formik.handleChange('has_year')}
                        />
                    </Stack>
                    <Stack alignItems="center">
                        <Typography variant="body2">{CONSTANTS.REQUEST_TOPIC.FIELDS.MONTH[lang]}</Typography>
                        <Checkbox 
                            checked={formik.values.has_month}
                            onChange={formik.handleChange('has_month')}
                        />
                    </Stack>
                    <Stack alignItems="center">
                        <Typography variant="body2">{CONSTANTS.REQUEST_TOPIC.FIELDS.DAY[lang]}</Typography>
                        <Checkbox 
                            checked={formik.values.has_day}
                            onChange={formik.handleChange('has_day')}
                        />
                    </Stack>
                </Stack>
            </Grid>
            <Grid
                item
                xs={4}
                alignContent={'center'}
            >   
                <Typography p={2}>{CONSTANTS.WAZAREN_FILTER.TAGS[lang]}</Typography>
            </Grid>
            <Grid
                item
                xs={8}
                sm={4}
                alignContent={'center'}
            >
                <Autocomplete
                    multiple
                    id="tags-standard"
                    freeSolo
                    filterSelectedOptions
                    options={tagKeys}
                    onChange={(e, newValue) =>updateTags(newValue)}
                    getOptionLabel={tagKey => CONSTANTS.WAZAREN_TAGS[tagKey][lang]}
                    renderTags={() => {}}
                    value={tags}
                    componentsProps={{
                        paper: {
                            sx: {
                                width: '100%'
                            }
                        }
                    }}
                    renderInput={params => (
                    <TextField
                        {...params}
                        variant="standard"
                        placeholder=""
                        margin="normal"
                        fullWidth
                    />
                    )}
                />
            </Grid>
            {
                isMobile?
                <Grid
                    item
                    xs={4}
                    alignContent={'center'}
                >
                </Grid>
                :
                <>
                </>
            }
            <Grid
                item
                xs={8}
                sm={4}
                alignContent={'center'}
            >
                <Stack 
                    className="selectedTags"
                    direction="row" 
                    spacing={1}
                    useFlexGap
                    flexWrap="wrap"
                >
                    {
                        tags.map((option, index) => {
                            // This is to handle new options added by the user (allowed by freeSolo prop).
                            const label = CONSTANTS.WAZAREN_TAGS[option][lang] || option;
                            return (
                                <Chip 
                                    size="medium"
                                    variant="outlined"
                                    key={option}
                                    label={label}
                                    deleteIcon={<RemoveIcon />} 
                                    onDelete={() => {
                                        updateTags(tags.filter(entry => entry !== option));
                                    }}
                                    sx={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        width: 'fit-content',
                                        borderRadius: '0.5rem',
                                        // backgroundColor: '#f0f0f0',
                                        borderColor: '#ccc',
                                    }}
                                />
                            );
                        })
                    }
                </Stack>
            </Grid>
            <Grid
                item
                xs={12}
                justifyContent={'left'}
            >
                <Typography
                    backgroundColor="#7e7e7e"
                    color="white"
                    fontWeight="bold"
                    p={2}
                >
                    {CONSTANTS.WAZAREN_DETAILS.VIDEO[lang]}
                </Typography>
            </Grid>
            <Grid
                item
                xs={4}
                alignContent={'center'}
            >   
                <Typography p={2}>
                    YouTube
                </Typography>
            </Grid>
            <Grid
                item
                xs={8}
                md={4}
                alignContent={'center'}
                pr={2}
            >   
                <TextField
                    name="YouTube"
                    variant="outlined"
                    placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.VIDEO_LINK.YOUTUBE[lang]}
                    type="text"
                    fullWidth
                    size="small"
                    error={Boolean(formik.touched.video_youtube && formik.errors.video_youtube)}
                    onChange={formik.handleChange('video_youtube')}
                    value={formik.values.video_youtube}
                />
            </Grid>
            { isMobile? <Grid item xs={4} />: <></> }
            <Grid
                item
                xs={8}
                md={4}
                alignContent={'center'}
                pr={2}
            >   
                <TextField
                    name="YouTube"
                    variant="outlined"
                    placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.VIDEO_REMARK.YOUTUBE[lang]}
                    type="text"
                    fullWidth
                    size="small"
                    error={Boolean(formik.touched.remark_youtube && formik.errors.remark_youtube)}
                    onChange={formik.handleChange('remark_youtube')}
                    value={formik.values.remark_youtube}
                />
            </Grid>
            <Grid
                item
                xs={4}
                alignContent={'center'}
            >   
                <Typography p={2}>
                    Bilibili
                </Typography>
            </Grid>
            <Grid
                item
                xs={8}
                md={4}
                alignContent={'center'}
                pr={2}
            >   
                <TextField
                    name="Bilibili"
                    variant="outlined"
                    placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.VIDEO_LINK.BILIBILI[lang]}
                    type="text"
                    fullWidth
                    size="small"
                    error={Boolean(formik.touched.video_bilibili && formik.errors.video_bilibili)}
                    onChange={formik.handleChange('video_bilibili')}
                    value={formik.values.video_bilibili}
                />
            </Grid>
            { isMobile? <Grid item xs={4} alignContent={'center'} />: <></> }
            <Grid
                item
                xs={8}
                md={4}
                alignContent={'center'}
                pr={2}
            >   
                <TextField
                    variant="outlined"
                    placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.VIDEO_REMARK.BILIBILI[lang]}
                    type="text"
                    fullWidth
                    size="small"
                    error={Boolean(formik.touched.remark_bilibili && formik.errors.remark_bilibili)}
                    onChange={formik.handleChange('remark_bilibili')}
                    value={formik.values.remark_bilibili}
                />
            </Grid>
            <Grid
                item
                xs={4}
                alignContent={'center'}
            >   
                <Typography p={2}>
                    X
                </Typography>
            </Grid>
            <Grid
                item
                xs={8}
                md={4}
                alignContent={'center'}
                pr={2}
            >   
                <TextField
                    name="X"
                    variant="outlined"
                    placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.VIDEO_LINK.X[lang]}
                    type="text"
                    fullWidth
                    size="small"
                    error={Boolean(formik.touched.video_x && formik.errors.video_x)}
                    onChange={formik.handleChange('video_x')}
                    value={formik.values.video_x}
                />
            </Grid>
            { isMobile? <Grid item xs={4} alignContent={'center'} />: <></> }
            <Grid
                item
                xs={8}
                md={4}
                alignContent={'center'}
                pr={2}
            >   
                <TextField
                    variant="outlined"
                    placeholder={CONSTANTS.REQUEST_TOPIC.PLACEHOLDERS.ENTER.VIDEO_REMARK.X[lang]}
                    type="text"
                    fullWidth
                    size="small"
                    error={Boolean(formik.touched.remark_x && formik.errors.remark_x)}
                    onChange={formik.handleChange('remark_x')}
                    value={formik.values.remark_x}
                />
            </Grid>
            
            {formik.errors.submit && (
                <Grid
                    item
                    xs={12}
                >
                <FormHelperText error>
                    {formik.errors.submit}
                </FormHelperText>
                </Grid>
            )}
        </Grid>
    )
}

export default FillInForm;